import TokenLogo from '@/components/token-logo'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { isInactiveDTF } from '@/hooks/use-dtf-status'
import { cn } from '@/lib/utils'
import { indexDTFAtom, indexDTFBrandAtom, indexDTFStatusAtom } from '@/state/dtf/atoms'
import { ROUTES } from '@/utils/constants'
import { t } from '@lingui/macro'
import { useAtomValue } from 'jotai'
import {
  ArrowLeftRight,
  Blend,
  CirclePlus,
  Fingerprint,
  Globe,
  Landmark,
  Wallet2,
} from 'lucide-react'
import { useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Address } from 'viem'
import { useAccount, useWatchAsset } from 'wagmi'

const NavigationItem = ({
  icon,
  label,
  route,
}: {
  icon: React.ReactNode
  label: string
  route: string
}) => {
  return (
    <NavLink to={route}>
      {({ isActive }) => (
        <div
          className={cn(
            'flex items-center transition-all rounded-full gap-2  hover:text-primary',
            isActive
              ? 'text-primary bg-primary/10 md:bg-transparent'
              : 'text-text'
          )}
        >
          {/* <div
            className={cn(
              'flex items-center justify-center rounded-full h-6 w-6 border border-border',
              isActive ? 'bg-primary/10' : 'bg-border'
            )}
          > */}
          <div className="h-10 w-10 md:h-6 md:w-6 flex items-center justify-center">
            {icon}
          </div>
          {/* </div> */}
          <div className="text-base hidden md:block">{label}</div>
        </div>
      )}
    </NavLink>
  )
}

const NavigationHeader = () => {
  const indexDTF = useAtomValue(indexDTFAtom)
  const brand = useAtomValue(indexDTFBrandAtom)
  const { address: walletAddress, chainId } = useAccount()
  const { watchAsset } = useWatchAsset()

  const handleWatchAsset = () => {
    if (!indexDTF || !walletAddress) return

    watchAsset({
      type: 'ERC20',
      options: {
        address: indexDTF.id as Address,
        symbol: indexDTF.token.symbol,
        decimals: indexDTF.token.decimals,
      },
    })
  }

  return (
    <div className="items-center gap-2 hidden lg:flex">
      <TokenLogo
        src={brand?.dtf?.icon || undefined}
        alt={indexDTF?.token.symbol ?? 'dtf token logo'}
        size="lg"
      />
      <div className="text-base font-semibold truncate">
        {indexDTF?.token.symbol}
      </div>
      {!!walletAddress && !!indexDTF && chainId === indexDTF.chainId && (
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto relative"
          onClick={handleWatchAsset}
        >
          <Wallet2 strokeWidth={1.5} size={16} />
          <CirclePlus
            strokeWidth={1.5}
            size={12}
            className="absolute bottom-2 right-1 bg-background"
          />
        </Button>
      )}
    </div>
  )
}

const DisabledNavigationItem = ({
  icon,
  label,
}: {
  icon: React.ReactNode
  label: string
}) => {
  return (
    <div className="opacity-50 pointer-events-none text-legend flex items-center transition-all rounded-full gap-2">
      <div className="h-10 w-10 md:h-6 md:w-6 flex items-center justify-center">
        {icon}
      </div>
      <div className="text-base hidden md:block">{label}</div>
    </div>
  )
}

const DISABLED_ROUTES_WHEN_DEPRECATED: string[] = [ROUTES.AUCTIONS]

const NavigationItems = () => {
  const dtf = useAtomValue(indexDTFAtom)
  const status = useAtomValue(indexDTFStatusAtom)
  const isDeprecated = isInactiveDTF(status)

  const items = useMemo(
    () => [
      {
        icon: <Globe strokeWidth={1.5} size={16} />,
        label: t`Overview`,
        route: ROUTES.OVERVIEW,
      },
      {
        icon: <Blend strokeWidth={1.5} size={16} />,
        label: t`Mint + Redeem`,
        route: ROUTES.ISSUANCE,
      },
      {
        icon: <Landmark strokeWidth={1.5} size={16} />,
        label: t`Governance`,
        route: ROUTES.GOVERNANCE,
      },
      {
        icon: <ArrowLeftRight strokeWidth={1.5} size={16} />,
        label: t`Auctions`,
        route: ROUTES.AUCTIONS,
      },
      {
        icon: <Fingerprint strokeWidth={1.5} size={16} />,
        label: t`Details + Roles`,
        route: ROUTES.SETTINGS,
      },
    ],
    [dtf?.token.symbol]
  )

  return (
    <div className="flex lg:flex-col gap-5 justify-evenly lg:justify-start">
      {items.map((item) =>
        isDeprecated &&
        DISABLED_ROUTES_WHEN_DEPRECATED.includes(item.route) ? (
          <DisabledNavigationItem
            key={item.route}
            icon={item.icon}
            label={item.label}
          />
        ) : (
          <NavigationItem key={item.route} {...item} />
        )
      )}
    </div>
  )
}

const IndexDTFNavigation = () => {
  const location = useLocation()
  const isFactsheet = location.pathname.endsWith(`/${ROUTES.FACTSHEET}`)

  if (isFactsheet) return null

  return (
    <div className="w-full lg:sticky lg:top-0 p-3 md:p-6 fixed bottom-0 border-t lg:border-t-0 lg:w-56 flex-shrink-0 bg-background z-[1] h-16 lg:h-full">
      <div className="sticky top-6">
        <NavigationHeader />
        <Separator className="my-4 hidden lg:block" />
        <NavigationItems />
      </div>
    </div>
  )
}

export default IndexDTFNavigation
